import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  CalendarDays, Clock, Loader2, X, MapPin, Ticket, Bell, AlertCircle, ChevronLeft, ChevronRight, CreditCard, Check
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { toast } from 'sonner';
import SavedPaymentMethods from '../components/SavedPaymentMethods';
import RyftPay3DSDialog from '../components/RyftPay3DSDialog';
import { WeekStrip } from '../components/schedule/WeekStrip';
import { SimilarClassesDialog } from '../components/schedule/SimilarClassesDialog';
import { apiGet, apiPost } from '../api';
import { recordBreadcrumb, reportError } from '../lib/sentry';
import { formatApiError } from '../lib/apiError';

export const SchedulePage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState({});
  const [classPasses, setClassPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingClass, setBookingClass] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [selectedPassId, setSelectedPassId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCard, setSelectedSavedCard] = useState('');
  const [showAddCardForBooking, setShowAddCardForBooking] = useState(false);
  
  // 3DS authentication state
  const [show3DSDialog, setShow3DSDialog] = useState(false);
  const [threeDSData, setThreeDSData] = useState(null);
  const [processing3DS, setProcessing3DS] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [cardFormInstance, setCardFormInstance] = useState(null);
  const [cardFormReady, setCardFormReady] = useState(false);
  
  // Similar classes state (shown after successful booking)
  const [showSimilarClasses, setShowSimilarClasses] = useState(false);
  const [similarClasses, setSimilarClasses] = useState([]);
  const [similarClassesLoading, setSimilarClassesLoading] = useState(false);
  const [bookedClassName, setBookedClassName] = useState('');
  
  // Basket for booking multiple classes in one transaction
  const [classBasket, setClassBasket] = useState([]);
  const [showBasketCheckout, setShowBasketCheckout] = useState(false);
  const [basketCheckoutLoading, setBasketCheckoutLoading] = useState(false);

  // Bug R23 fix (Iter-62): voucher input state for single-class booking.
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherValidation, setVoucherValidation] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);

  // Start date for the 7-day window
  const [startDate, setStartDate] = useState(new Date());
  
  // Generate 7 days from start date
  const get7Days = (start) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  };

  const [days, setDays] = useState(get7Days(new Date()));
  const [selectedDay, setSelectedDay] = useState(days[0]);
  
  // Navigate forward/backward by 7 days
  const handlePrevWeek = () => {
    const newStart = subDays(startDate, 7);
    setStartDate(newStart);
    const newDays = get7Days(newStart);
    setDays(newDays);
    setSelectedDay(newDays[0]);
  };
  
  const handleNextWeek = () => {
    const newStart = addDays(startDate, 7);
    setStartDate(newStart);
    const newDays = get7Days(newStart);
    setDays(newDays);
    setSelectedDay(newDays[0]);
  };

  const fetchPublicKey = async () => {
    try {
      // Typed SDK — public_key field is auto-checked against PaymentConfigResponse.
      const data = await apiGet('/api/payments/config');
      if (data.public_key) {
        setPublicKey(data.public_key);
      }
    } catch (e) {
      console.error('Failed to fetch payment config:', e);
    }
  };

  const fetchSavedCards = async () => {
    try {
      const data = await apiGet('/api/payment-methods');
      setSavedCards(data || []);
      const defaultCard = data?.find(c => c.is_default);
      if (defaultCard) {
        setSelectedSavedCard(defaultCard.id);
      }
    } catch (e) {
      console.error('Failed to fetch saved cards:', e);
    }
  };

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const start = format(days[0], 'yyyy-MM-dd');
      const end = format(days[6], 'yyyy-MM-dd');

      // If authenticated, use api (with auth token) to filter by favourites
      if (isAuthenticated) {
        const data = await apiGet('/api/classes/schedule', { query: { start_date: start, end_date: end } });
        setClasses(data.classes_by_date || {});
      } else {
        // Public view - show all classes
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/classes/schedule?start_date=${start}&end_date=${end}`
        );
        const data = await response.json();
        setClasses(data.classes_by_date || {});
      }
    } catch (e) {
      console.error('Failed to fetch schedule:', e);
    } finally {
      setLoading(false);
    }
  }, [days, isAuthenticated]);

  const fetchClassPasses = useCallback(async () => {
    try {
      const data = await apiGet('/api/user-class-passes');
      setClassPasses(data.filter(p => p.status === 'active'));
    } catch (e) {
      console.error('Failed to fetch class passes:', e);
    }
  }, []);

  // Iter-46an: must come AFTER fetchSchedule/fetchClassPasses useCallbacks
  // are declared — otherwise the [fetchSchedule, fetchClassPasses] deps
  // array hits a Temporal Dead Zone on first render.
  useEffect(() => {
    fetchSchedule();
    if (isAuthenticated) {
      fetchClassPasses();
      fetchSavedCards();
      fetchPublicKey();

      // Check if returning from 3DS redirect
      const pending3DSData = localStorage.getItem('pending_3ds_booking');
      if (pending3DSData) {
        try {
          const pendingData = JSON.parse(pending3DSData);
          localStorage.removeItem('pending_3ds_booking');

          // Confirm the booking with our backend
          apiPost('/api/bookings/confirm-3ds', { query: { pending_booking_id: pendingData.pendingBookingId } })
            .then(() => {
              toast.success('Payment verified! Class booked successfully!');
              fetchSchedule();
              fetchClassPasses();
            })
            .catch((e) => {
              console.error('3DS confirmation error:', e);
              toast.error(formatApiError(e, 'Payment verification failed. Please try again or contact support.'));
            });
        } catch (e) {
          console.error('Error processing 3DS return:', e);
          localStorage.removeItem('pending_3ds_booking');
        }
      }
    }
  }, [isAuthenticated, fetchSchedule, fetchClassPasses]);

  const handleBookClass = async (cls) => {
    if (!isAuthenticated) {
      toast.error('Please login to book a class');
      return;
    }
    // Refresh saved cards and pre-select default when opening booking dialog
    try {
      const freshCards = await apiGet('/api/payment-methods');
      setSavedCards(freshCards || []);
      const defaultCard = freshCards?.find(c => c.is_default);
      if (defaultCard) {
        setSelectedSavedCard(defaultCard.id);
      } else if (freshCards && freshCards.length > 0) {
        setSelectedSavedCard(freshCards[0].id);
      }
    } catch (e) {
      console.log('Could not fetch saved cards:', e);
    }
    setBookingClass(cls);
    setSelectedPassId(null); // Reset pass selection
    // Bug R23 fix: reset voucher state per open so a stale voucher
    // doesn't leak into a different class.
    setVoucherCode('');
    setVoucherValidation(null);
  };

  const confirmBooking = async () => {
    if (!bookingClass) return;
    
    const useClassPass = selectedPassId !== null;
    const paySingleClass = !useClassPass && bookingClass.price > 0;
    
    // If paying single class rate and no card selected, that's okay
    // The payment dialog will collect card details via RyftPay SDK
    
    setBookingLoading(true);
    recordBreadcrumb('booking', 'User confirming class booking', {
      class_id: bookingClass.id,
      studio_id: bookingClass.studio_id,
      class_name: bookingClass.name,
      use_class_pass: useClassPass,
      class_pass_id: useClassPass ? selectedPassId : null,
      pay_single_class: paySingleClass,
    });
    try {
      const bookingData = {
        class_id: bookingClass.id,
        studio_id: bookingClass.studio_id,
        use_class_pass: useClassPass,
        class_pass_id: useClassPass ? selectedPassId : null,
        pay_single_class: paySingleClass,
        payment_method_id: paySingleClass ? (selectedSavedCard || null) : null,
        // Bug R23 fix: forward the voucher code only when paying with card
        // (vouchers do not apply to ClassPass-covered bookings).
        voucher_code: paySingleClass && voucherValidation?.valid ? voucherCode.trim().toUpperCase() : null,
      };
      
      const response = await apiPost('/api/bookings', { body: bookingData });
      
      // Check if 3DS authentication is required
      if (response.status === 'requires_action' && response.client_secret) {
        // Iter-42 Phase 1: fetch customer's saved cards for one-click pay.
        let customerPaymentMethods = [];
        try {
          customerPaymentMethods = await apiGet('/api/payments/customer/methods');
        } catch (_) { /* fall through to direct card entry */ }
        // Store 3DS data and show authentication dialog
        setThreeDSData({
          clientSecret: response.client_secret,
          pendingBookingId: response.pending_booking_id,
          paymentSessionId: response.payment_session_id,
          redirectUrl: response.redirect_url,
          requiredAction: response.required_action,
          savedCard: response.saved_card,
          accountId: response.account_id,  // Sub-account ID for platform fees
          // Iter-46ar: backend signals whether the session needs forced save.
          // Single-class bookings are Standard paymentType → optional save.
          requireSaveCard: !!response.require_save_card,
          className: bookingClass.name,
          studioId: bookingClass.studio_id,
          classId: bookingClass.id,
          classStartTime: bookingClass.start_time,
          customerPaymentMethods,
        });
        setShow3DSDialog(true);
        setBookingLoading(false);
        return;
      }
      
      // Direct success (no 3DS needed)
      toast.success('Class booked successfully!');
      
      // Store the class name for similar classes lookup
      const className = bookingClass.name;
      const studioId = bookingClass.studio_id;
      const classId = bookingClass.id;
      const classStartTime = bookingClass.start_time;
      
      setBookingClass(null);
      setSelectedPassId(null);
      fetchSchedule();
      fetchClassPasses();
      
      // Fetch similar upcoming classes with the same name (only AFTER the booked class)
      try {
        setSimilarClassesLoading(true);
        const query = {
          name: className,
          studio_id: studioId,
          exclude_id: classId,
          limit: '10',
        };
        if (classStartTime) query.after_time = classStartTime;
        const data = await apiGet('/api/classes/similar', { query });
        if (data && data.length > 0) {
          setSimilarClasses(data);
          setBookedClassName(className);
          setShowSimilarClasses(true);
        }
      } catch (e) {
        // Silently fail - similar classes is a nice-to-have
        console.log('Could not fetch similar classes:', e);
      } finally {
        setSimilarClassesLoading(false);
      }
    } catch (e) {
      reportError(e, {
        flow: 'class_booking',
        action: 'confirm',
        extra: {
          class_id: bookingClass?.id,
          studio_id: bookingClass?.studio_id,
          use_class_pass: useClassPass,
          pay_single_class: paySingleClass,
        },
      });
      const detail = formatApiError(e, '');
      toast.error(detail || 'Failed to book class');
    } finally {
      setBookingLoading(false);
    }
  };

  // Mount payment form when 3DS dialog opens
  useEffect(() => {
    if (!show3DSDialog || !threeDSData || !publicKey) {
      setCardFormInstance(null);
      setCardFormReady(false);
      return;
    }

    if (!threeDSData.clientSecret || !threeDSData.clientSecret.startsWith('ps_')) {
      console.error('Invalid clientSecret:', threeDSData.clientSecret);
      return;
    }

    let mounted = true;
    
    const mountPaymentForm = async () => {
      // Wait for dialog to render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (!mounted) return;

      // Wait for SDK
      let attempts = 0;
      while (!window.Ryft || typeof window.Ryft.createController !== 'function') {
        attempts++;
        if (attempts > 30 || !mounted) return;
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const container = document.getElementById('ryft-3ds-container');
      if (!container || !mounted) return;

      container.innerHTML = '';

      const { createController, createCardForm } = window.Ryft;

      console.log('Creating RyftPay one-time payment form');
      console.log('Public Key:', publicKey?.substring(0, 25) + '...');
      console.log('Client Secret:', threeDSData.clientSecret?.substring(0, 55) + '...');
      console.log('Account ID (sub-account):', threeDSData.accountId || 'none');
      console.log('Customer payment methods:', threeDSData.customerPaymentMethods?.length || 0, 'cards');

      try {
        // Create controller for ONE-TIME payment
        // Include accountId if payment session was created with platform fee
        const controllerConfig = {
          publicKey: publicKey,
          clientSecret: threeDSData.clientSecret
        };
        
        // Add accountId for platform fee sessions (required by RyftPay)
        if (threeDSData.accountId) {
          controllerConfig.accountId = threeDSData.accountId;
        }
        
        const controller = createController(controllerConfig);

        // Iter-44 fix: per Ryft Embedded SDK v3 docs, `details` MUST be the
        // full `{ items: [...] }` response from `customerGetPaymentMethods`.
        // Passing a bare array silently disables saved-card chips.
        const customerCardDetails = threeDSData.customerPaymentMethods;
        const hasSavedCards =
          customerCardDetails &&
          Array.isArray(customerCardDetails.items) &&
          customerCardDetails.items.length > 0;
        console.log('Mounting cardForm with', hasSavedCards ? customerCardDetails.items.length : 0, 'saved cards');
        const cardFormConfig = {
          customerPaymentMethods: {
            // Iter-70: 'Save this card' checkbox removed from checkout —
            // cards must be saved via Profile > Billing so we get a
            // CIT verifyAccount=true card usable for POS MIT + auto-renew.
            // Existing saved cards still appear as one-click chips.
            allowStorage: { enabled: false },
            ...(hasSavedCards ? { details: customerCardDetails } : {}),
          },
          validationMode: 'onBlur',
        };
        const cardForm = createCardForm(controller, cardFormConfig);

        // Listen for validation changes - this tells us when card details are valid
        cardForm.on('validationChange', (event) => {
          console.log('Card form validation changed:', event.isValid);
          if (mounted) {
            setCardFormReady(event.isValid);
          }
        });

        // When user picks a saved card chip, enable Pay immediately.
        cardForm.on('paymentMethodChange', (event) => {
          console.log('Card form paymentMethodChange:', event);
          if (mounted) setCardFormReady(true);
        });

        cardForm.on('error', (error) => {
          // Iter-46ar: forward the full SDK error to our backend log so we can
          // see WHICH Ryft API call failed and with what payload without
          // requiring browser DevTools. Best-effort, never throws.
          console.error('Card form error:', error);
          try {
            const payload = {
              kind: 'cardform_error_schedule',
              message: error?.message || error?.code || String(error) || 'unknown',
              error: (() => { try { return JSON.parse(JSON.stringify(error)); } catch { return null; } })(),
              client_secret_prefix: typeof threeDSData?.clientSecret === 'string' ? threeDSData.clientSecret.slice(0, 8) : null,
              account_id: threeDSData?.accountId || null,
              is_class_pass_purchase: !!threeDSData?.isClassPassPurchase,
              is_basket: !!threeDSData?.isBasketCheckout,
            };
            const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/payments/sdk-error';
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
          } catch (_) { /* swallow */ }
        });

        // Mount the form
        cardForm.mount(container);
        console.log('Card form mounted to container');
        
        if (mounted) {
          setCardFormInstance(cardForm);
        }
      } catch (e) {
        console.error('Error creating payment form:', e);
        if (mounted) {
          toast.error('Failed to load payment form: ' + e.message);
        }
      }
    };

    mountPaymentForm();

    return () => {
      mounted = false;
    };
  }, [show3DSDialog, threeDSData, publicKey]);

  // Handle payment submission
  const handle3DSAuthentication = async () => {
    if (!cardFormInstance) {
      toast.error('Payment form not ready. Please wait or try again.');
      return;
    }

    setProcessing3DS(true);
    
    try {
      // If redirect URL provided, use that
      if (threeDSData?.redirectUrl) {
        localStorage.setItem('pending_3ds_booking', JSON.stringify({
          pendingBookingId: threeDSData.pendingBookingId,
          className: threeDSData.className,
          studioId: threeDSData.studioId,
          classId: threeDSData.classId
        }));
        window.location.href = threeDSData.redirectUrl;
        return;
      }

      // Attempt payment using the mounted form
      console.log('Calling attemptPayment...');
      const result = await cardFormInstance.attemptPayment();
      console.log('Payment result:', JSON.stringify(result, null, 2));
      
      const status = result?.paymentSession?.status;
      const sessionId = result?.paymentSession?.id || threeDSData.paymentSessionId;

      if (status === 'Approved' || status === 'Captured') {
        // Iter-44: fire-and-forget sync — if the user ticked the SDK's
        // save-card checkbox, mirror the new pmt to our local DB so it shows
        // in /payment-methods and is available for studio-admin POS MIT.
        if (sessionId) {
          apiPost('/api/payment-methods/sync-from-session', { body: { payment_session_id: sessionId } })
            .catch((e) => console.warn('save-card sync skipped:', e?.message || e));
        }

        // Confirm with backend — endpoint depends on flow type
        if (threeDSData.isBasketCheckout) {
          const confirmResult = await apiPost('/api/bookings/confirm-batch-3ds', { query: { pending_booking_id: threeDSData.pendingBookingId } });
          toast.success(`Payment successful! Booked ${confirmResult.count} ${confirmResult.count === 1 ? 'class' : 'classes'}.`);
          setShow3DSDialog(false);
          setThreeDSData(null);
          setClassBasket([]);
          setShowSimilarClasses(false);
          fetchSchedule();
          fetchClassPasses();
          return;
        }

        if (threeDSData.isClassPassPurchase) {
          await apiPost('/api/user-class-passes/confirm-3ds', { query: { pending_purchase_id: threeDSData.pendingPurchaseId } });
          toast.success('ClassPass purchased successfully!');
          setShow3DSDialog(false);
          setThreeDSData(null);
          if (typeof threeDSData.onSuccess === 'function') {
            threeDSData.onSuccess();
          }
          return;
        }

        // Single-class booking confirm
        await apiPost('/api/bookings/confirm-3ds', { query: { pending_booking_id: threeDSData.pendingBookingId } });

        toast.success('Payment successful! Class booked!');
        setShow3DSDialog(false);
        setThreeDSData(null);
        setBookingClass(null);
        setSelectedPassId(null);
        fetchSchedule();
        fetchClassPasses();

        // Fetch similar classes (only AFTER the booked class)
        try {
          const query = {
            name: threeDSData.className,
            studio_id: threeDSData.studioId,
            exclude_id: threeDSData.classId,
            limit: '10',
          };
          if (threeDSData.classStartTime) query.after_time = threeDSData.classStartTime;
          const data = await apiGet('/api/classes/similar', { query });
          if (data && data.length > 0) {
            setSimilarClasses(data);
            setBookedClassName(threeDSData.className);
            setShowSimilarClasses(true);
          }
        } catch (e) {
          console.log('Could not fetch similar classes:', e);
        }
      } else if (status === 'Declined' || status === 'Failed') {
        toast.error(result?.userFacingErrorMessage || 'Payment was declined.');
      } else {
        toast.error(result?.userFacingErrorMessage || `Payment status: ${status}. Please try again.`);
      }
    } catch (e) {
      reportError(e, {
        flow: threeDSData?.isClassPassPurchase ? 'class_pass_purchase' : 'class_booking',
        action: '3ds_confirm',
        extra: {
          pending_booking_id: threeDSData?.pendingBookingId,
          pending_purchase_id: threeDSData?.pendingPurchaseId,
          is_basket: !!threeDSData?.isBasketCheckout,
          is_class_pass_purchase: !!threeDSData?.isClassPassPurchase,
          ryft_error_code: e?.code,
          ryft_error_type: e?.type,
        },
      });
      console.error('Payment error full details:', e);
      // Iter-46ar: dump the FULL details array because the user's bug screenshot
      // showed "details: (1) [...]" without the actual entry — and that's the
      // Ryft attempt-payment 400 response we need.
      try {
        console.error('Error.details JSON:', JSON.stringify(e?.details, null, 2));
      } catch (jsonErr) {
        console.error('Error.details not JSON-serialisable:', e?.details);
      }
      console.error('Error properties:', {
        message: e.message,
        userFacingErrorMessage: e.userFacingErrorMessage,
        code: e.code,
        type: e.type,
        details: e.details
      });
      // Iter-46ar: forward to backend log too so we capture details without DevTools.
      try {
        const payload = {
          kind: 'schedule_attempt_payment_throw',
          message: e?.message || String(e),
          code: e?.code || null,
          type: e?.type || null,
          details: (() => { try { return JSON.parse(JSON.stringify(e?.details)); } catch { return null; } })(),
          error: (() => { try { return JSON.parse(JSON.stringify(e)); } catch { return null; } })(),
          client_secret_prefix: typeof threeDSData?.clientSecret === 'string' ? threeDSData.clientSecret.slice(0, 8) : null,
          account_id: threeDSData?.accountId || null,
          is_class_pass_purchase: !!threeDSData?.isClassPassPurchase,
          is_basket: !!threeDSData?.isBasketCheckout,
        };
        const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/payments/sdk-error';
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      } catch (_) { /* swallow */ }

      // Iter-46ar: translate the noisy "[Ryft card-form Error] API Error: 400 - <uuid>"
      // message to something the user can actually act on.
      const friendly = e.userFacingErrorMessage || e.message;
      const looksLikeReqId =
        typeof friendly === 'string' &&
        /API Error:\s*\d+\s*-\s*[a-f0-9-]{20,}/i.test(friendly);
      if (looksLikeReqId) {
        toast.error(
          'Your bank declined the payment or the card was not accepted. ' +
            'Please double-check the card details and try again, or use a different card.'
        );
      } else {
        toast.error(friendly || 'Payment failed. Please try again.');
      }
    } finally {
      setProcessing3DS(false);
    }
  };

  // Cancel 3DS and close dialog
  const cancel3DS = () => {
    // Clear all payment-related state
    const wasBasket = threeDSData?.isBasketCheckout;
    setShow3DSDialog(false);
    setThreeDSData(null);
    setCardFormInstance(null);
    setCardFormReady(false);
    setProcessing3DS(false);
    if (wasBasket) {
      // Keep the basket so the user can retry, but close the similar-classes overlay
      // so they're not stuck on a stale modal.
    }
    toast.info('Payment cancelled');
  };

  const handleCardAddedForBooking = () => {
    fetchSavedCards();
    setShowAddCardForBooking(false);
    toast.success('Card added! Click Book Class to continue.');
  };

  // Add class to basket instead of booking directly
  const addToBasket = (cls) => {
    // Iter-70: block adding a class the user is already booked in.
    if (cls.is_booked) {
      toast.info('You are already booked for this class');
      return;
    }
    // Check if already in basket
    if (classBasket.find(c => c.id === cls.id)) {
      toast.info('Class already in basket');
      return;
    }
    setClassBasket(prev => [...prev, cls]);
    toast.success(`Added ${cls.name} to basket`);
  };

  // Remove class from basket
  const removeFromBasket = (classId) => {
    setClassBasket(prev => prev.filter(c => c.id !== classId));
  };

  // Calculate basket total
  const basketTotal = classBasket.reduce((sum, cls) => sum + (cls.price || 0), 0);

  // Bug R14 fix (Iter-64): find an active ClassPass that covers EVERY class
  // in the current basket. When found, checkoutBasket will send its id so
  // the backend redeems credits for all classes rather than forcing the
  // user to pay again. Only single-studio baskets qualify (matches the
  // batch endpoint's studio_id constraint).
  const basketApplicablePassId = (() => {
    if (classBasket.length === 0) return null;
    const studioIds = [...new Set(classBasket.map(c => c.studio_id))];
    if (studioIds.length > 1) return null;
    const studioId = studioIds[0];
    for (const p of (classPasses || [])) {
      if (p.status !== 'active') continue;
      if (p.studio_id !== studioId) continue;
      if ((p.passes_remaining || 0) < classBasket.length) continue;
      // Applies_to matches every class in the basket
      const appliesAll = classBasket.every(cls =>
        !p.applicable_activity_types || p.applicable_activity_types.length === 0 ||
        p.applicable_activity_types.includes(cls.activity_type)
      );
      if (appliesAll) return p.id;
    }
    return null;
  })();

  // Checkout basket - creates one payment for all classes
  const checkoutBasket = async () => {
    if (classBasket.length === 0) {
      toast.error('Basket is empty');
      return;
    }

    // All classes must be from the same studio for platform fee routing
    const studioIds = [...new Set(classBasket.map(c => c.studio_id))];
    if (studioIds.length > 1) {
      toast.error('All classes must be from the same studio for checkout');
      return;
    }

    setBasketCheckoutLoading(true);
    recordBreadcrumb('booking', 'User checking out class basket', {
      studio_id: studioIds[0],
      class_count: classBasket.length,
      total: basketTotal,
      pass_id: basketApplicablePassId,
    });
    try {
      const data = await apiPost('/api/bookings/batch', {
        body: {
          studio_id: studioIds[0],
          classes: classBasket.map(cls => ({ class_id: cls.id })),
          // Bug R14 fix: send the applicable pass id so the backend
          // redeems credits for the entire basket instead of asking the
          // user to pay for classes 2+.
          class_pass_id: basketApplicablePassId,
        },
      });

      // Free or completed directly (no payment needed)
      if (data.status === 'completed') {
        toast.success(`Successfully booked ${data.count} ${data.count === 1 ? 'class' : 'classes'}!`);
        setClassBasket([]);
        setShowSimilarClasses(false);
        fetchSchedule();
        fetchClassPasses();
        return;
      }

      // Payment requires 3DS
      if (data.status === 'requires_action' && data.client_secret) {
        // Iter-42 Phase 1: fetch saved cards for one-click pay.
        let customerPaymentMethods = [];
        try {
          customerPaymentMethods = await apiGet('/api/payments/customer/methods');
        } catch (_) { /* fall through to direct card entry */ }
        setThreeDSData({
          clientSecret: data.client_secret,
          pendingBookingId: data.pending_booking_id,
          paymentSessionId: data.payment_session_id,
          accountId: data.account_id,
          // Iter-46ar: basket bookings are Standard paymentType — optional save.
          requireSaveCard: !!data.require_save_card,
          className: `${classBasket.length} ${classBasket.length === 1 ? 'class' : 'classes'} (€${basketTotal.toFixed(2)})`,
          studioId: studioIds[0],
          classId: classBasket[0].id,
          isBasketCheckout: true,
          basketClasses: [...classBasket],
          customerPaymentMethods,
        });
        setShow3DSDialog(true);
      }
    } catch (e) {
      reportError(e, {
        flow: 'class_booking',
        action: 'basket_checkout',
        extra: {
          studio_id: studioIds[0],
          class_count: classBasket.length,
          total: basketTotal,
        },
      });
      const detail = formatApiError(e, '');
      toast.error(detail || 'Failed to checkout');
    } finally {
      setBasketCheckoutLoading(false);
    }
  };

  const bookSimilarClass = (cls) => {
    // Legacy alias retained — basket flow is now handled via addToBasket / checkoutBasket
    addToBasket(cls);
  };

  const handleJoinWaitlist = async (cls) => {
    if (!isAuthenticated) {
      toast.error('Please login to join waitlist');
      return;
    }
    try {
      const resp = await apiPost('/api/waitlist', { body: {
        class_id: cls.id,
        studio_id: cls.studio_id
      } });
      // Iter-46am: format the grace window into something human-readable
      // and show it on the success toast so users know up-front how long
      // they'll have to grab a freed seat. 0 means immediate public access.
      const mins = resp?.waitlist_grace_minutes ?? 60;
      const formatGrace = (m) => {
        if (m <= 0) return null;
        if (m < 60) return `${m} minutes`;
        if (m === 60) return '1 hour';
        return m % 60 === 0 ? `${m / 60} hours` : `${Math.floor(m / 60)}h ${m % 60}m`;
      };
      const graceText = formatGrace(mins);
      toast.success(
        graceText
          ? `You're on the waitlist! If a seat opens up you'll have ${graceText} to book it before it's offered to everyone.`
          : "You're on the waitlist! If a seat opens up everyone will be notified at the same time — first to book wins.",
        { duration: 7000 }
      );
      fetchSchedule();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to join waitlist'));
    }
  };

  const selectedDateStr = format(selectedDay, 'yyyy-MM-dd');

  // Iter-46: derive weekly classes grouped by date for the week-view layout.
  const totalWeekClasses = days.reduce(
    (acc, d) => acc + ((classes[format(d, 'yyyy-MM-dd')] || []).length),
    0
  );

  // Find applicable ClassPasses for a class
  const getApplicablePasses = (cls) => {
    return classPasses.filter(pass => 
      pass.studio_id === cls.studio_id && 
      (pass.activity_type === cls.activity_type || pass.activity_type === 'general') &&
      pass.passes_remaining > 0
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="schedule-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">Class Schedule</h1>
          <p className="text-muted-foreground">Browse and book classes across all studios</p>
        </div>

        {/* Weekly Day Slider with Navigation — extracted to <WeekStrip> in Iter-46aj */}
        <WeekStrip
          days={days}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          classes={classes}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
        />

        {/* Classes grouped by Day across the week (Iter-46) */}
        <div className="space-y-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : totalWeekClasses === 0 ? (
            <Card className="p-12 text-center">
              <CalendarDays className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground text-lg">No classes scheduled for this week</p>
              <p className="text-sm text-muted-foreground mt-1">Try a different week using the arrows above</p>
            </Card>
          ) : (
            days.map((day, dIdx) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const clsList = classes[dayStr] || [];
              const isToday = format(new Date(), 'yyyy-MM-dd') === dayStr;
              return (
                <section
                  key={dayStr}
                  id={`day-section-${dayStr}`}
                  data-testid={`day-section-${dIdx}`}
                  className="scroll-mt-24"
                >
                  <div className="flex items-baseline justify-between border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      {format(day, 'EEEE, MMMM d')}
                      {isToday && (
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-primary/20" variant="outline">
                          Today
                        </Badge>
                      )}
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      {clsList.length === 0 ? 'No classes' : `${clsList.length} ${clsList.length === 1 ? 'class' : 'classes'}`}
                    </span>
                  </div>

                  {clsList.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic pl-1">
                      No classes scheduled
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {clsList.map((cls, i) => {
                        const isFull = cls.is_full || cls.current_bookings >= cls.max_capacity;
                        const spotsLeft = cls.max_capacity - (cls.current_bookings || 0);
                        const isPast = new Date(cls.start_time) < new Date();
                        const isBooked = !!cls.is_booked;

                        return (
                          <Card
                            key={cls.id}
                            className={isPast ? 'opacity-60' : ''}
                            data-testid={`schedule-class-${dIdx}-${i}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Badge variant="outline" className="capitalize">
                                      {cls.activity_type || 'General'}
                                    </Badge>
                                    <h3 className="font-semibold text-lg">{cls.name}</h3>
                                    {isPast && (
                                      <Badge variant="secondary">Past</Badge>
                                    )}
                                    {!isPast && isBooked && (
                                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Booked</Badge>
                                    )}
                                    {!isPast && !isBooked && isFull && (
                                      <Badge variant="destructive">Full</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-4 h-4" />
                                      {format(new Date(cls.start_time), 'h:mm a')} - {format(new Date(cls.end_time), 'h:mm a')}
                                    </span>
                                    {cls.instructor_name && (
                                      <span>with {cls.instructor_name}</span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {cls.studio_name}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3">
                                  {!isPast && (
                                    <>
                                      <div className="text-right">
                                        {cls.price > 0 && (
                                          <p className="font-semibold text-primary">€{cls.price}</p>
                                        )}
                                        <p className={`text-sm ${isFull ? 'text-destructive' : 'text-muted-foreground'}`}>
                                          {isFull ? 'No spots' : `${spotsLeft} spots left`}
                                        </p>
                                      </div>

                                      {isBooked ? (
                                        <Button
                                          variant="outline"
                                          disabled
                                          data-testid={`already-booked-btn-${dIdx}-${i}`}
                                        >
                                          Already Booked
                                        </Button>
                                      ) : isFull ? (
                                        <Button
                                          variant="outline"
                                          onClick={() => handleJoinWaitlist(cls)}
                                          data-testid={`waitlist-btn-${dIdx}-${i}`}
                                        >
                                          <Bell className="w-4 h-4 mr-2" />
                                          Join Waitlist
                                        </Button>
                                      ) : (
                                        <Button
                                          onClick={() => handleBookClass(cls)}
                                          data-testid={`book-btn-${dIdx}-${i}`}
                                        >
                                          Book Now
                                        </Button>
                                      )}
                                    </>
                                  )}
                                  {isPast && (
                                    <p className="text-sm text-muted-foreground">This class has ended</p>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>

      {/* Booking Dialog */}
      {bookingClass && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="booking-dialog">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Book Class</CardTitle>
              <CardDescription>
                {bookingClass.name} - {format(new Date(bookingClass.start_time), 'h:mm a')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{bookingClass.name}</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(bookingClass.start_time), 'EEEE, MMMM d')} at {format(new Date(bookingClass.start_time), 'h:mm a')}
                </p>
                <p className="text-sm text-muted-foreground">{bookingClass.studio_name}</p>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Choose Payment Method:</Label>
                
                {/* Option 1: Use ClassPass (if available) */}
                {getApplicablePasses(bookingClass).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Ticket className="w-4 h-4" />
                      Use a ClassPass:
                    </p>
                    {getApplicablePasses(bookingClass).map((pass) => (
                      <div 
                        key={pass.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPassId === pass.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'hover:border-muted-foreground'
                        }`}
                        onClick={() => setSelectedPassId(selectedPassId === pass.id ? null : pass.id)}
                        data-testid={`select-pass-${pass.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{pass.class_pass_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {pass.passes_remaining} passes remaining
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedPassId === pass.id && (
                              <Badge className="bg-primary">Selected</Badge>
                            )}
                            <Ticket className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Option 2: Pay Single Class Rate */}
                {bookingClass.price > 0 && (
                  <div className="space-y-3">
                    <div 
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedPassId === null ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'hover:border-muted-foreground'
                      }`}
                      onClick={() => setSelectedPassId(null)}
                      data-testid="pay-single-rate"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Pay Single Class Rate</p>
                          <p className="text-sm text-muted-foreground">One-time drop-in payment</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedPassId === null && (
                            <Badge className="bg-primary">Selected</Badge>
                          )}
                          <span className="text-xl font-bold text-primary">€{bookingClass.price}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Iter-43: removed the screen-1 saved-card picker for paid
                        single-class bookings. The picked card was never actually
                        used by the backend (the Standard CIT session always
                        opens the SDK card form for the user to confirm). The SDK
                        form now shows saved cards as one-click chips inside it
                        (Phase 1), so this screen-1 picker only created confusion. */}
                    {selectedPassId === null && (
                      <div className="ml-4 p-3 rounded-lg border border-dashed bg-muted/30">
                        <p className="text-sm text-muted-foreground">
                          {savedCards.length > 0
                            ? "You'll confirm payment with your saved card on the next step."
                            : "You'll be prompted to add a payment card on the next step."}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* No ClassPasses Available - Show Buy Button */}
                {getApplicablePasses(bookingClass).length === 0 && bookingClass.price > 0 && (
                  <div className="p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">No ClassPass on file for this class</p>
                        <p className="text-xs text-amber-700">
                          Pay the single-class rate above, or browse this studio&apos;s ClassPasses to save on future bookings.
                        </p>
                      </div>
                    </div>
                    {(bookingClass.studio_slug || bookingClass.studio_id) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => {
                          const target = bookingClass.studio_slug || bookingClass.studio_id;
                          setBookingClass(null);
                          // Bug #6 fix: route is /studio/:slug (singular). The old
                          // /studios/{slug} path fell through the catch-all Route
                          // and dumped the user on the homepage.
                          navigate(`/studio/${target}?tab=classpasses`);
                        }}
                        data-testid="buy-classpass-btn"
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        Browse this studio&apos;s ClassPasses
                      </Button>
                    )}
                  </div>
                )}

                {/* Free Class Info */}
                {bookingClass.price === 0 && getApplicablePasses(bookingClass).length === 0 && (
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm text-emerald-700 font-medium">This is a free class!</p>
                    <p className="text-xs text-emerald-600">No payment required</p>
                  </div>
                )}

                {/* Bug R23 fix (Iter-62): voucher input for card-pay flow. */}
                {bookingClass.price > 0 && !selectedPassId && (
                  <div className="space-y-2 border-t pt-3">
                    <label htmlFor="booking-voucher-input" className="text-sm font-medium">Voucher code (optional)</label>
                    <div className="flex gap-2">
                      <input
                        id="booking-voucher-input"
                        type="text"
                        className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm uppercase"
                        placeholder="Enter voucher"
                        value={voucherCode}
                        onChange={(e) => {
                          setVoucherCode(e.target.value.toUpperCase());
                          setVoucherValidation(null);
                        }}
                        data-testid="booking-voucher-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={validatingVoucher || !voucherCode.trim()}
                        onClick={async () => {
                          setValidatingVoucher(true);
                          try {
                            const res = await apiPost('/api/vouchers/validate', {
                              body: {
                                code: voucherCode.trim().toUpperCase(),
                                purchase_type: 'single_class',
                                amount: bookingClass.price,
                                studio_id: bookingClass.studio_id,
                              }
                            });
                            setVoucherValidation(res);
                            if (res.valid) toast.success(res.message || 'Voucher applied');
                            else toast.error(res.message || 'Invalid voucher');
                          } catch (_) {
                            setVoucherValidation({ valid: false, message: 'Invalid voucher code' });
                            toast.error('Invalid voucher code');
                          } finally {
                            setValidatingVoucher(false);
                          }
                        }}
                        data-testid="apply-voucher-btn"
                      >
                        {validatingVoucher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {voucherValidation?.valid && (
                      <p className="text-xs text-emerald-700" data-testid="voucher-applied-msg">
                        Voucher applied — €{voucherValidation.discount_amount?.toFixed(2)} off, you pay €{voucherValidation.final_amount?.toFixed(2)}
                      </p>
                    )}
                    {voucherValidation && !voucherValidation.valid && (
                      <p className="text-xs text-destructive">{voucherValidation.message}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button variant="outline" onClick={() => setBookingClass(null)} data-testid="cancel-booking-dialog">
                Cancel
              </Button>
              <Button 
                onClick={() => confirmBooking()}
                disabled={bookingLoading}
                data-testid="confirm-booking-btn"
              >
                {bookingLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {selectedPassId
                  ? 'Use ClassPass'
                  : bookingClass.price > 0
                    ? `Pay €${voucherValidation?.valid ? voucherValidation.final_amount?.toFixed(2) : bookingClass.price}`
                    : 'Confirm Booking'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Similar Classes Dialog — extracted to <SimilarClassesDialog> in Iter-46aj */}
      <SimilarClassesDialog
        open={showSimilarClasses}
        bookedClassName={bookedClassName}
        similarClasses={similarClasses}
        classBasket={classBasket}
        basketTotal={basketTotal}
        basketCheckoutLoading={basketCheckoutLoading}
        basketApplicablePassId={basketApplicablePassId}
        onClose={() => { setShowSimilarClasses(false); setClassBasket([]); }}
        onAddToBasket={addToBasket}
        onRemoveFromBasket={removeFromBasket}
        onJoinWaitlist={handleJoinWaitlist}
        onCheckout={checkoutBasket}
      />

      {/* Add Card for Booking Dialog */}
      <Dialog open={showAddCardForBooking} onOpenChange={setShowAddCardForBooking}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Add a card to pay for {bookingClass?.name} (€{bookingClass?.price})
            </DialogDescription>
          </DialogHeader>
          <SavedPaymentMethods onCardAdded={handleCardAddedForBooking} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCardForBooking(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3DS Authentication Dialog */}
      <Dialog open={show3DSDialog} onOpenChange={(open) => !open && cancel3DS()} modal={false}>
        <DialogContent
          className="sm:max-w-lg"
          // Iter-43 fix: don't dismiss when the user interacts with the Ryft SDK's
          // 3DS challenge iframe / save-card checkbox rendered outside the dialog DOM.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Complete Your Payment
            </DialogTitle>
            <DialogDescription>
              Please enter your card details to complete this booking. Your bank may require additional verification.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Booking:</p>
              <p className="font-medium">{threeDSData?.className}</p>
            </div>

            {/* Container for RyftPay form */}
            <div id="ryft-3ds-container" className="min-h-[250px] border rounded-lg p-3"></div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={cancel3DS}
              disabled={processing3DS}
            >
              Cancel
            </Button>
            <Button 
              onClick={handle3DSAuthentication}
              disabled={processing3DS || !cardFormReady}
              data-testid="complete-3ds-btn"
            >
              {processing3DS ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : !cardFormReady ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Pay Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
